import {
  BodyText,
  Button,
  Card,
  Heading,
  Icon,
  IconSet,
  Layout,
  LinkText,
  ModalBody,
} from '@postscript/components';
import { DateTime } from 'luxon';
import styled from 'styled-components';

import { useAssignProductPlanFreeTrial } from 'components/billing/context/useBilling';
import { useInfinityTestingBillingStatus } from 'components/billing/modules/plans/hooks/useInfinityTestingBillingStatus';
import { useGetBrandingByType } from 'components/brand/hooks/useBranding';
import { useGlobalModal } from 'components/GlobalModal/globalModal';

import ContinueToBrandCenterInfinityTestingModal from 'components/billing/modules/plans/ContinueToBrandCenterInfinityTestingModal';
import WelcomeToInfinityTestingModal from 'components/billing/modules/plans/WelcomeToInfinityTestingModal';

import { ITC_PLAN_ID } from 'components/billing/common/constants';

import { StyledDivider } from 'components/billing/modules/plans/styles';
import { useUser } from 'controllers/contexts/user';
import {
  INFINITY_TESTING_TRIED_STEP_KEY,
  PS_APP_PROG_ONBOARDING_TOUCHLESS_FEATURE_KEY,
} from 'providers/ProgressiveOnboarding/constants/constants';
import { useMarkStepCompletedIfAvailable } from 'providers/ProgressiveOnboarding/utils';
import {
  COMMON_EVENT_NAMES,
  logEvent,
  MODAL_TYPES,
  PRODUCT_AREAS,
} from 'utils/events';

const StyledContainer = styled.div`
  display: flex;
`;

const StyledLeftContainer = styled.div`
  background-color: var(--gray-2);
  background-image: radial-gradient(var(--gray-3) 1px, transparent 0.1px);
  background-size: 7px 7px;
  background-position: -19px -19px;
  flex: 5;
  padding: var(--spacing-6);
`;

const StyledRightContainer = styled.div`
  background-color: var(--gray-1);
  flex: 4;
  padding: var(--spacing-6);
`;

const StyledRightContent = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: space-between;
`;

const StyledDetail = styled.div`
  align-items: top;
  display: flex;
  justify-content: space-between;
  margin-top: var(--spacing-4);
  width: 100%;
`;

const StyledCard = styled(Card)`
  padding-left: var(--spacing-2);
  padding-right: var(--spacing-2);
  margin-bottom: var(--spacing-3);
  margin-top: var(--spacing-3);
`;

const StyledFormerPrice = styled.span`
  color: var(--gray-4);
  font-size: 14px;
  font-weight: bold;
  margin-right: var(--spacing-1);
  text-decoration: line-through;
`;

const StyledFooterDetail = styled.span`
  display: flex;
  justify-content: flex-end;
  margin-top: var(--spacing-4);
`;
const StyledCancelBtn = styled(Button)`
  margin-right: var(--spacing-2);
`;

const CardContent = [
  {
    icon: IconSet.TrendingUp,
    heading: 'Increase Revenue',
    detail: (
      <div>
        Infinity Testing drives an average of <strong>20% revenue lift </strong>
        for Automations and a <strong>38% lift in EPM</strong> for Campaigns.
      </div>
    ),
  },
  {
    icon: IconSet.Rocket,
    heading: 'Evolve your SMS program',
    detail: (
      <div>
        Infinity Testing continuously explores, tests, and optimizes on-brand
        messages to <strong>achieve maximum revenue.</strong>
      </div>
    ),
  },
  {
    icon: IconSet.Message,
    heading: 'Valuable customer insights',
    detail: (
      <div>
        Review message performance to gain insight into what strategies resonate
        and apply learnings across your SMS program and other channels.
      </div>
    ),
  },
];

const proratedAmount = (amount: number) => {
  // add 1 to daysLeftInMonth to include current day
  const daysLeftInMonth =
    DateTime.local().daysInMonth - DateTime.local().day + 1;
  const dailyRate = amount / DateTime.local().daysInMonth;
  return (dailyRate * daysLeftInMonth).toFixed(2);
};

const InfinityTestingModal = () => {
  const { user } = useUser();
  const { showModal, hideModal } = useGlobalModal();
  const { trialExpired } = useInfinityTestingBillingStatus();
  const markStepCompleted = useMarkStepCompletedIfAvailable();

  // By setting days to 0 a shop is affectively buying the product today
  const freeTrialDays = trialExpired ? 0 : 30;

  const { data: marketingBranding } = useGetBrandingByType('marketing');
  const { mutateAsync: assignProductPlanFreeTrial } =
    useAssignProductPlanFreeTrial({
      successMessage:
        freeTrialDays > 0
          ? 'Successfully started trial.'
          : 'Successfully added product.',
    });

  const handleStartTrial = async (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    markStepCompleted({
      guideKey: PS_APP_PROG_ONBOARDING_TOUCHLESS_FEATURE_KEY,
      stepKey: INFINITY_TESTING_TRIED_STEP_KEY,
    });
    logEvent(COMMON_EVENT_NAMES.MODAL_CTA_CLICKED, {
      modal_type: MODAL_TYPES.INFINITY_TESTING,
      product_area: PRODUCT_AREAS.BILLING,
      shop_id: user.shop_id,
      text: (event.target as HTMLButtonElement).innerText,
    });

    await assignProductPlanFreeTrial({
      id: ITC_PLAN_ID,
      productType: 'MARKETING_AI',
      freeTrialDays,
    });

    hideModal();

    logEvent(COMMON_EVENT_NAMES.MODAL_OPENED, {
      modal_type: MODAL_TYPES.INFINITY_TESTING,
      product_area: PRODUCT_AREAS.BILLING,
      shop_id: user.shop_id,
      text: marketingBranding
        ? 'Welcome to Infinity Testing'
        : 'Continue to Brand Center',
    });

    showModal({
      children: marketingBranding ? (
        <WelcomeToInfinityTestingModal />
      ) : (
        <ContinueToBrandCenterInfinityTestingModal />
      ),
      style: { width: 700, height: 627, padding: 0 },
    });
  };

  const handleCancel = (event: React.MouseEvent<HTMLButtonElement>) => {
    logEvent(COMMON_EVENT_NAMES.MODAL_DISMISSED, {
      modal_type: MODAL_TYPES.INFINITY_TESTING,
      product_area: PRODUCT_AREAS.BILLING,
      shop_id: user.shop_id,
      text: (event.target as HTMLButtonElement).innerText,
    });

    hideModal();
  };

  return (
    <ModalBody>
      <StyledContainer>
        <StyledLeftContainer>
          <Heading size="large">
            {`${trialExpired ? 'Add' : 'Try'} Infinity Testing`}
          </Heading>
          <BodyText size="large" style={{ marginTop: 'var(--spacing-1)' }}>
            {trialExpired
              ? 'Get access to unlimited Infinity Testing Campaigns and Automations'
              : 'Get access to Infinity Testing Campaigns and Automations for 30 days'}
          </BodyText>
          <BodyText
            size="medium"
            bold
            style={{ marginTop: 'var(--spacing-2)' }}
          >
            No contract required. Cancel any time.
          </BodyText>
          {CardContent.map(({ icon, heading, detail }) => {
            return (
              <StyledCard key={heading} size="small">
                <Layout valign="top">
                  <Icon component={icon} />
                  <div>
                    <Heading size="xx-small">{heading}</Heading>
                    <BodyText size="small">{detail}</BodyText>
                  </div>
                </Layout>
              </StyledCard>
            );
          })}
          {!trialExpired && (
            <BodyText size="x-small" bold>
              *Add-On fee is waived for 30 days. Message fees still apply.
            </BodyText>
          )}
        </StyledLeftContainer>
        <StyledRightContainer>
          <StyledRightContent>
            <div>
              <Layout align="right">
                <Icon
                  component={IconSet.Close}
                  size="medium"
                  onClick={hideModal}
                  style={{ cursor: 'pointer' }}
                />
              </Layout>
              <Heading size="x-small">Details</Heading>
              <StyledDetail>
                <div>
                  <BodyText size="small" bold>
                    {trialExpired ? 'Monthly Fee' : 'Add-On Fee'}
                  </BodyText>
                  <BodyText size="x-small">
                    {`${
                      trialExpired ? '' : 'Waived for 30 days, then'
                    } $599/month`}
                  </BodyText>
                </div>
                <div style={{ paddingLeft: 'var(--spacing-4)' }}>
                  {!trialExpired && <StyledFormerPrice>$599</StyledFormerPrice>}
                  <BodyText bold size="medium" as="span">
                    {trialExpired ? '$599' : '$0.00'}
                  </BodyText>
                </div>
              </StyledDetail>
              <StyledDetail>
                <div>
                  <BodyText size="small" bold>
                    Infinity Testing Campaign Surcharge Rate
                  </BodyText>
                  <BodyText size="x-small">
                    {trialExpired
                      ? 'Only pay for winning campaigns. 10% of total base message and carrier fees.'
                      : 'Waived for 30 days, then 10% of total base message and carrier fees *'}
                  </BodyText>
                </div>
                <BodyText
                  size="medium"
                  bold
                  as="span"
                  style={{ paddingLeft: 'var(--spacing-6)' }}
                >
                  {!trialExpired && <StyledFormerPrice>10%</StyledFormerPrice>}
                  {trialExpired ? '10%' : '0%'}
                </BodyText>
              </StyledDetail>
              <StyledDetail>
                <div>
                  <BodyText size="small" bold>
                    Infinity Testing Automation Surcharge Rate
                  </BodyText>
                  <BodyText size="x-small">
                    Additional cost per automation message, added to base
                    message and carrier fees.
                  </BodyText>
                </div>
                <BodyText
                  size="medium"
                  bold
                  as="span"
                  style={{ paddingLeft: 'var(--spacing-6)' }}
                >
                  $0.009
                </BodyText>
              </StyledDetail>
              <StyledDivider />
              <StyledDetail>
                <BodyText size="medium" bold>
                  Billed today
                  <BodyText size="x-small" as="span">
                    {trialExpired ? 'Pro-rated Monthly Fee' : ''}
                  </BodyText>
                </BodyText>
                <BodyText size="medium" bold as="span">
                  {trialExpired ? `$${proratedAmount(599)}` : '$0.00'}
                </BodyText>
              </StyledDetail>
            </div>
            <div>
              <StyledFooterDetail>
                <div>
                  <BodyText size="small" as="span">
                    {trialExpired
                      ? 'By adding to plan you agree to '
                      : 'By starting trial you agree to '}
                  </BodyText>
                  <LinkText
                    href="https://postscript.io/terms-of-service"
                    target="_blank"
                    style={{ fontSize: '14px' }}
                  >
                    Postscript’s terms of service
                  </LinkText>
                </div>
              </StyledFooterDetail>
              <StyledFooterDetail>
                <StyledCancelBtn variant="text" onClick={handleCancel}>
                  Cancel
                </StyledCancelBtn>
                <Button onClick={handleStartTrial}>
                  {trialExpired ? 'Add to Plan' : 'Start trial'}
                </Button>
              </StyledFooterDetail>
            </div>
          </StyledRightContent>
        </StyledRightContainer>
      </StyledContainer>
    </ModalBody>
  );
};

export default InfinityTestingModal;
